
import { useState, useEffect, memo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSessionContext } from "@supabase/auth-helpers-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import MainContent from "./layout/MainContent";
import { Header } from "@/components/home/Header";
import { Footer } from "@/components/home/Footer";
import { BorrowerSidebar } from "./borrower/BorrowerSidebar";

interface LayoutProps {
  children: React.ReactNode;
}

const Layout = memo(({ children }: LayoutProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [userRole, setUserRole] = useState<'borrower' | 'lender' | 'admin' | 'vendor' | null>(null);
  const { session } = useSessionContext();
  const { toast } = useToast();

  useEffect(() => {
    const fetchUserRole = async () => {
      if (!session?.user?.id) {
        setUserRole(null);
        return;
      }

      // Try to get role from user metadata first (faster)
      const metadataRole = session.user.user_metadata?.role;
      if (metadataRole) {
        console.log("Using role from user metadata:", metadataRole);
        setUserRole(metadataRole as 'borrower' | 'lender' | 'admin' | 'vendor');
        return;
      }

      // Fall back to database query if metadata doesn't have role
      try {
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', session.user.id)
          .single();

        if (error) {
          console.error('Error fetching user role:', error);
          toast({
            title: "Error",
            description: "Could not fetch user role. Please try signing in again.",
            variant: "destructive",
          });
          await supabase.auth.signOut();
          navigate('/signin');
          return;
        }

        if (profile?.role) {
          setUserRole(profile.role as 'borrower' | 'lender' | 'admin' | 'vendor');
          console.log("User role set to:", profile.role);
        } else {
          toast({
            title: "Error",
            description: "User role not found. Please contact support.",
            variant: "destructive",
          });
          await supabase.auth.signOut();
          navigate('/signin');
        }
      } catch (error) {
        console.error('Unexpected error:', error);
      }
    };

    fetchUserRole();
  }, [session, navigate, toast]);

  // Routes that use the public Header component
  const publicRoutes = ['/signin', '/register', '/lender/signin', '/lender/register', '/vendor/signin', '/vendor/register', '/'];
  
  // Routes that use regular Header but are not dashboard routes
  const publicContentRoutes = ['/borrowers', '/shop', '/vendors', '/lenders', '/real-estate', '/about', '/cart'];
  
  // Check if current route starts with any of these paths
  const isPublicContentPage = publicContentRoutes.some(route => 
    location.pathname === route || location.pathname.startsWith(`${route}/`)
  );

  // Check if this is a borrower route - explicitly check for any route starting with "/borrower"
  const isBorrowerRoute = location.pathname.startsWith('/borrower');
  
  // Debug info
  console.log("Current route:", location.pathname);
  console.log("Is borrower route:", isBorrowerRoute);
  console.log("User role:", userRole);
  console.log("Session:", !!session);

  // First handle the simplest case - public routes
  if (publicRoutes.includes(location.pathname)) {
    return <div className="min-h-screen w-full bg-gradient-to-br from-background to-background/95">{children}</div>;
  }

  // Second case - public content pages with header and footer
  if (isPublicContentPage) {
    return (
      <div className="min-h-screen w-full bg-gradient-to-br from-background to-background/95">
        <Header />
        <div className="pt-16">{children}</div>
        <Footer />
      </div>
    );
  }

  // Third case - borrower routes with sidebar for borrower users
  if (isBorrowerRoute && userRole === 'borrower') {
    console.log("Rendering borrower sidebar");
    return (
      <div className="min-h-screen w-full flex bg-gradient-to-br from-background to-background/95">
        <BorrowerSidebar />
        <div className="flex-1 overflow-y-auto ml-[70px] md:ml-[240px]">
          <div className="p-4 sm:p-6 md:p-8">{children}</div>
        </div>
      </div>
    );
  }

  // Fourth case - borrower routes but user role still loading or not a borrower
  if (isBorrowerRoute) {
    // Wait for the role to load
    if (session && !userRole) {
      return (
        <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-background to-background/95">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
        </div>
      );
    }
    
    // Not authorized as borrower, but trying to access borrower routes
    if (userRole && userRole !== 'borrower') {
      navigate('/');
      return null;
    }
  }

  // Default case - main content with appropriate top navigation
  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-background to-background/95">
      <MainContent
        userRole={userRole}
        session={!!session}
        isMobile={isMobile}
      >
        {children}
      </MainContent>
      {userRole && <Footer />}
    </div>
  );
});

Layout.displayName = 'Layout';

export default Layout;
